// Camera controllers for WORLD COSTA BLANCA.
//
// SOURCE REPO: Juanmaes83/escaparates-pro (labs/immersive-worlds, branch
//   claude/immersive-worlds-module-c0d3f7, commit 6e6d6ca)
// SOURCE PATHS: engine/camera/controllers/{explore,focus,directed}-controller.js
// CAPABILITY: controller lifecycle contract (onGain/onLose/update+commit).
// METHOD: REPURPOSE (contract) + EXTEND (modes taxonomy per Juanmaes83/nightdrive
//   camera-mode thinking: overview / walk / map / focus / directed).
// CHANGES MADE: controllers are rewritten for this runtime: OrbitControls is
//   bound to a virtual camera so the real camera is only ever written by the
//   render host from the authoritative pose; Walker never touches the camera.
// WHY: one camera writer per frame is a hard product invariant.
// DEPENDENCIES: three OrbitControls (virtual camera), runtime/walker.js.
// RISKS: OrbitControls damping only advances while the controller owns the
//   camera; on handoff the pose is re-synced in onGain.

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

function applyPoseToCamera(pose, camera, target) {
  camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
  if (Number.isFinite(pose.fov) && Math.abs(camera.fov - pose.fov) > 1e-3) {
    camera.fov = pose.fov;
    camera.updateProjectionMatrix();
  }
  if (target) target.set(pose.target[0], pose.target[1], pose.target[2]);
  else camera.lookAt(pose.target[0], pose.target[1], pose.target[2]);
}

function poseFromCamera(camera, target) {
  return {
    position: [camera.position.x, camera.position.y, camera.position.z],
    target: [target.x, target.y, target.z],
    fov: camera.fov
  };
}

/** WORLD OVERVIEW / free orbit. OrbitControls drives a VIRTUAL camera; the
 *  authoritative pose is read back and committed. The real scene camera is
 *  never touched here. */
export class OrbitController {
  constructor(dom) {
    this.virtual = new THREE.PerspectiveCamera(42, 1, 0.1, 500);
    this.target = new THREE.Vector3(0, 2, 4);
    this.controls = new OrbitControls(this.virtual, dom);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.065;
    this.controls.minDistance = 8;
    this.controls.maxDistance = 75;
    this.controls.maxPolarAngle = Math.PI * 0.48;
    this.controls.minPolarAngle = 0.25;
    this.controls.enablePan = true;
    this.controls.screenSpacePanning = true;
    this.controls.enabled = false;
  }

  onGain(pose) {
    applyPoseToCamera(pose, this.virtual, this.target);
    this.controls.enabled = true;
    this.controls.update();
  }

  onLose() {
    this.controls.enabled = false;
  }

  update(_dt, commit) {
    this.controls.update();
    commit(poseFromCamera(this.virtual, this.target));
  }
}

/** WALK_SOFT_POV — sakura-grade first person behind the authority. */
export class WalkController {
  constructor(walker) {
    this.walker = walker;
  }

  onGain(pose) {
    // Adopt horizontal position + heading from the incoming pose so the
    // transition lands exactly where the camera was looking.
    const [px, , pz] = pose.position;
    const [tx, , tz] = pose.target;
    const dx = tx - px, dz = tz - pz;
    if (Math.hypot(dx, dz) > 1e-3) {
      this.walker.setPose({ yaw: Math.atan2(-dx, -dz) });
    }
    this.walker.enabled = true;
  }

  onLose() {
    this.walker.enabled = false;
    this.walker.keys.clear();
  }

  update(dt, commit) {
    this.walker.update(dt);
    commit(this.walker.pose());
  }
}

/** Static held pose (PROPERTY_APPROACH / PROPERTY_HERO / MAP_TOPDOWN /
 *  DIRECTED guided stops). The authority's transition system lands the pose;
 *  this controller simply holds it. */
export class HeldPoseController {
  constructor(name) {
    this.name = name;
    this.pose = null;
  }

  setPose(pose) {
    this.pose = {
      position: [...pose.position],
      target: [...pose.target],
      fov: pose.fov ?? 42
    };
  }

  onGain(pose) {
    if (!this.pose) this.setPose(pose);
    else {
      // The authority hands us the final pose of the transition; hold ours.
      this.pose.fov = this.pose.fov ?? pose.fov;
    }
  }

  update(_dt, commit) {
    if (this.pose) commit(this.pose);
  }
}
